import {parseAppZoneDate} from '@/utils/dateUtils'

/**
 * READING A TRIP'S DELAYS — which one is running, when it ends, and whether the passenger
 * may report another.
 *
 * ── ⚠️ EVERY TIMESTAMP HERE GOES THROUGH `parseAppZoneDate` ───────────────────────
 * The backend sends both `2026-09-14 15:40:00` (Toronto, no zone) and
 * `2026-09-14T15:40:00-04:00`. A bare `new Date()` reads the first one in the VIEWER's
 * zone, so a passenger in Vancouver sees a 15-minute delay expire three hours ago and a
 * passenger in London sees it expire in five hours. `parseAppZoneDate` reads a zone-less
 * string as app-zone wall clock and passes an explicit offset straight through.
 *
 * The mobile app has this bug in one place — `DelaySystemBubble` formats `requested_at`
 * with a plain `new Date` — so the displayed times there are wrong off Eastern. Do not
 * port that part.
 */

/** Milliseconds since epoch, or null. The one place a server timestamp becomes a number. */
function atMs(value) {
    const date = parseAppZoneDate(value)
    const ms = date?.getTime()
    return Number.isFinite(ms) ? ms : null
}

/**
 * WHEN THE WINDOW CLOSES.
 *
 * ⚠️ RECONSTRUCTED IF THE SERVER'S `expires_at` IS MISSING OR UNREADABLE, rather than
 * treated as "no deadline". `requested_at + minutes` is the same arithmetic the server
 * did, and a delay with no computable end would otherwise count as permanently expired —
 * which re-offers the entry point to a passenger who has just used it.
 *
 * @returns {number|null} epoch ms
 */
export function delayExpiresAtMs(delay) {
    if (!delay) return null

    const explicit = atMs(delay.expires_at)
    if (explicit != null) return explicit

    const requested = atMs(delay.requested_at)
    const minutes = Number(delay.minutes)
    if (requested == null || !(minutes > 0)) return null
    return requested + minutes * 60000
}

/**
 * Is this delay still running?
 *
 * ⚠️ COMPUTED FROM THE CLOCK, NOT READ FROM `is_active`. The flag is a snapshot of what
 * was true when the payload was built; a trip cached three minutes ago still says a
 * long-finished delay is active. The deadline is the only field that stays true on its own.
 */
export function isDelayActive(delay) {
    const expiry = delayExpiresAtMs(delay)
    return expiry != null && expiry > Date.now()
}

/**
 * THE DELAY CURRENTLY RUNNING, or null.
 *
 * ⚠️ `active_requested_delay` IS CHECKED, NOT TRUSTED. The mobile code carries a comment
 * about exactly this: a refetch moments after a successful report came back with that
 * field `null` even though the delay existed, which re-offered the "Experiencing a delay?"
 * card seconds after the passenger had used it. So the history is searched as a fallback,
 * and the record that expires LAST wins — a passenger who extended has two unexpired rows
 * and the later deadline is the real one.
 */
export function resolveActiveDelay(trip) {
    if (!trip) return null
    if (isDelayActive(trip.active_requested_delay)) return trip.active_requested_delay

    let latest = null
    let latestExpiry = -Infinity
    for (const delay of trip.requested_delays || []) {
        if (!isDelayActive(delay)) continue
        const expiry = delayExpiresAtMs(delay) ?? -Infinity
        if (expiry > latestExpiry) {
            latestExpiry = expiry
            latest = delay
        }
    }
    return latest
}

/**
 * The reported delays as feed rows, OLDEST FIRST — the order a conversation is read in,
 * where the payload is newest first.
 *
 * ⚠️ A BAD TIMESTAMP CARRIES FORWARD RATHER THAN FALLING TO ZERO. An unparseable
 * `requested_at` sorted to epoch 0 would drag that whole exchange to the top of the thread,
 * above messages from days later. Taking "one after the last known" keeps it where it
 * actually happened in the sequence.
 */
export function delayFeedItems(delays) {
    if (!delays?.length) return []

    let last = 0
    return [...delays].reverse().map((delay) => {
        const ms = atMs(delay.requested_at) ?? last + 1
        last = Math.max(last, ms)
        return {id: `delay-${delay.id}`, atMs: ms, delay}
    })
}

/**
 * MAY THE PASSENGER REPORT A DELAY RIGHT NOW?
 *
 * ⚠️ IT IS A FROM-AIRPORT, PRE-RIDE CONTROL AND NOTHING ELSE. A delay moves the pickup
 * time, so it is meaningless once the driver has the passenger in the car, and it is
 * meaningless on a trip TO an airport where the passenger is the one setting off.
 *
 * ⚠️ AND IT NEEDS THE FLIGHT TO HAVE LANDED. Offering it to someone still in the air is
 * offering to delay a pickup they have not begun walking to. See `useFlightLandedPassed`.
 *
 * ⚠️ ONE AT A TIME. The server rejects a second delay while one is running
 * (`requested_delay_active`), so the entry point hides itself rather than letting a
 * passenger tap into a guaranteed error. The countdown banner takes its place.
 *
 * @param {object|null} trip
 * @param {{liveStatusEvent?: string, flightLandedPassed?: boolean}} context
 */
export function canReportDelay(trip, {liveStatusEvent = '', flightLandedPassed = false} = {}) {
    if (!trip) return false
    if (trip.trip_direction !== 'from_airport') return false
    if (isTripFinished(trip)) return false
    if (trip.status === 'InProgress') return false
    /* The socket can say the ride started before the polled trip payload catches up, and
       between those two the entry point would still be offered. */
    if (/started/i.test(liveStatusEvent || '')) return false
    if (!flightLandedPassed) return false
    return resolveActiveDelay(trip) == null
}

/**
 * Completed or cancelled, read from whatever the payload happens to carry.
 *
 * ⚠️ THE TEXT IS SEARCHED AS WELL AS THE FIELDS, because `status` and `status_label` are
 * free-form enough that the same state arrives as `Completed`, `completed` or
 * `Trip completed`. Word boundaries, so `uncancelled` never matches `cancelled`.
 */
function isTripFinished(trip) {
    if (trip.completed_at || trip.cancelled_at || trip.cancelled_by) return true
    const text = `${trip.status || ''} ${trip.status_label || ''}`.toLowerCase()
    return /\b(completed|finished|done|closed|delivered|past|cancelled|canceled|cancel)\b/.test(text)
}
